import { Link } from "@tanstack/react-router";
import { Project } from "../../utils/interface/project";
import { LayoutGrid, FolderOpen } from "lucide-react";
import dummyProjectImage from "../../assets/Projects/project1.png";
import { useAuth } from "../../contexts/AuthContext";

// Dummy/placeholder image when project has no thumbnail (same as Dashboard project card)
const DUMMY_IMAGE_FALLBACK = dummyProjectImage;

interface ProjectListing {
  projectList: Project[] | [];
  handleEdit: (project: Project) => void;
  handleDelete: (project: Project) => void;
  handleView: (project: Project) => void;
  loading: boolean;
  isDeleteModal: boolean;
}

const Listing = ({
  projectList,
  handleEdit,
  handleDelete,
  handleView,
  loading,
  isDeleteModal,
}: ProjectListing) => {
  const { userDetails } = useAuth();

  if (loading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center min-h-[280px]">
        <div className="relative">
          <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
          <LayoutGrid className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-white/90 text-base mt-5 font-medium">
          Loading projects...
        </p>
        <p className="text-gray-500 text-sm mt-1">
          Fetching your project list
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {projectList.length === 0 ? (
        <div className="text-center py-20 px-6">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl border border-[#E761A0]/30 bg-[#E761A0]/5 mb-6">
            <FolderOpen className="w-12 h-12 text-[#E761A0]" />
          </div>
          <h3 className="text-white text-lg font-semibold mb-2">
            No projects yet
          </h3>

        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:gap-x-[49px] gap-x-[20px] gap-y-[35px] lg:gap-y-[55px] xl:gap-y-[82px] mb-10">
          {projectList.map((project: Project) => (
            <div
              key={project.id}
              className="rounded-xl overflow-hidden  transition-colors duration-200 flex flex-col max-w-[364px] project-card"
            >
              {/* Card image with dummy fallback */}
              <div className="relative w-full aspect-[5/3] overflow-hidden shrink-0 rounded-[18px] min-h-[234px]">
                {(project?.projectThumbnailPreview ?? project?.projectThumbnail) ? (
                  <img
                    src={((project?.projectThumbnailPreview ?? project?.projectThumbnail) || undefined)}
                    alt={project?.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = DUMMY_IMAGE_FALLBACK;
                    }}
                  />
                ) : (
                  <img
                    src={DUMMY_IMAGE_FALLBACK}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Card content */}
              <div className="pt-6 flex flex-col flex-1">
                <h3 className="text-[20px] 2xl:text-[22px] font-semibold text-white mb-3 lg:mb-4 xl:mb-6 line-clamp-1">
                  {project.name}
                </h3>
                {project.location && (
                  <p className=" text-sm lg:text-base text-gray-400 mb-1">
                    {project.location || ""}
                  </p>
                )}
                <p className=" text-base lg:text-lg text-white line-clamp-3 mb-6 flex-1 font-weight-300">
                  {project.summary ||
                    ""}
                </p>

                {/* Actions: VIEW PROJECT + Edit/Delete */}
                <div className="flex items-stretch sm:items-center gap-2 mt-auto justify-between">
                  <Link
                    to="/project-management/view/$id"
                    params={{ id: project.id }}
                    state={{ project }}
                    className="theme-btn   sm:min-w-[140px] md:min-w-[175px] inline-flex items-center justify-center py-3 sm:py-2 text-sm sm:text-base shrink-0 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]"
                    aria-label="View project"
                  >
                    VIEW PROJECT
                  </Link>
                  {userDetails?.type === "0" && (
                    <button
                      type="button"
                      className="group flex items-center justify-center gap-1.5 sm:gap-2 rounded-[27px] border-[2px] border-[#65449B] bg-[#65449B] text-white px-4 sm:px-6 py-3 sm:py-[11px] text-sm sm:text-[16px] font-semibold tracking-wide transition-all duration-300 ease-out hover:bg-transparent hover:shadow-none hover:border-[#65449B]   sm:w-auto sm:flex-1 sm:max-w-[148px] shrink-0 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]"
                      onClick={() => handleEdit(project)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="26.997"
                        height="26.953"
                        viewBox="0 0 26.997 26.953"
                        className="text-white group-hover:text-purple-400 transition-all duration-300 ease-out"
                        fill="currentColor"
                      >
                        <path
                          id="Path_235"
                          data-name="Path 235"
                          d="M22.392,16.076a1.4,1.4,0,0,1,2.8,0v7.2a3.685,3.685,0,0,1-3.674,3.674H3.674A3.685,3.685,0,0,1,0,23.279V5.318A3.685,3.685,0,0,1,3.674,1.644h7.678a1.4,1.4,0,0,1,0,2.8H3.674a.877.877,0,0,0-.875.875V23.279a.877.877,0,0,0,.875.875H21.516a.877.877,0,0,0,.875-.875ZM23.206.332l3.46,3.46a1.141,1.141,0,0,1,0,1.608L15.27,16.8l-5.854.827.765-5.874L21.6.332a1.141,1.141,0,0,1,1.608,0"
                        />
                      </svg>
                      <span className="uppercase tracking-wide">EDIT</span>
                    </button>
                  )}

                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Listing;
